import React from "react";
import { Stack, Avatar, Typography } from "@mui/material";
import { TargetsIcon } from "routes";
import EditorTable from "common/components/Table/editor";
import ActionButton from "common/components/ActionButton";
import SidePanel from "common/components/SidePanel";
import useScopeAttributes from "common/hooks/useScopeAttributes";
import SelectPlatform from "pages/Targets/components/ModifyTarget/SelectPlatform";
import useFetcher from "common/hooks/useFetcher";
import toast from "react-hot-toast";
import useState from "common/hooks/useState";
import FileUpload from "common/components/FileUpload";
export default function AddTenant({ tenants, refresh, onClose }) {
  const fetcher = useFetcher();
  const [confirmed, setConfirmed] = React.useState(null);
  const [platformConfig, setPlatformConfig] = React.useState([
    ["property", "value"],
  ]);
  const [tenantConfig, setTenantConfig] = React.useState([
    ["property", "value"],
  ]);
  const { tenant: e1tenant, loading, error } = useState("/api/meta");
  const { ScopeInputs, getScope, setInit } = useScopeAttributes();
  const [panelView, setPanelView] = React.useState(false);
  const [platform, setPlatform] = React.useState("");
  const [tenant, setTenant] = React.useState("");
  const [configuration, setConfiguration] = React.useState(null);
  const fetchPlatformConfig = async () => {
    try {
      await fetcher.get(
        "fetching tenant configuration",
        `/api/platforms/${platform.toLowerCase()}/tenant/config`,
        (config) => {
          if (!config)
            return toast.error("There was an issue getting the tenant config");
          let platformConfig = [
            ["property", "value"],
            ...Object.entries(config).map(([k, v]) => {
              return { property: k, value: "" };
            }),
          ];
          setPlatformConfig(config);
          setTenantConfig(platformConfig);
        },
        () => {}
      );
    } catch {}
  };
  React.useEffect(() => {
    if (!platform) return;
    setConfirmed(false);
    //get platform config file and load it
    fetchPlatformConfig();
  }, [platform]);
  const addTenant = async () => {
    let tc = tenantConfig
      .slice(1)
      .reduce((a, c) => ({ ...a, [c.property]: c.value }), {});
    let alreadyExists = tenants.map((t) => t.id).includes(tc.id);
    if (alreadyExists) {
      return toast.error("Tenant id already exists");
    }
    await fetcher.post(
      "adding tenant",
      `/api/tenants`,
      () => {
        let form = new FormData();
        form.append(
          "json",
          JSON.stringify({
            type: platform,
            tenantConfig: tc,
            ...getScope(),
          })
        );
        // if (configuration) form.append("File", configuration);
        return form;
      },
      ({ id }, toast) => {
        if (id) {
          toast.success("Tenant added!");
          refresh();
          return onClose();
        }
        toast.error(
          "Looks like we had an issue. If the issue persists, please contact support."
        );
      },
      (e) => {}
    );
  };
  const confirmTenant = async () => {
    let tc = tenantConfig
      .slice(1)
      .reduce((a, c) => ({ ...a, [c.property]: c.value }), {});
    if (
      Object.keys(platformConfig)
        .sort()
        .join(",") !==
      Object.keys(tc)
        .sort()
        .join(",")
    ) {
      setConfirmed(false);
      return toast.error("tenant configuration is not valid");
    }
    setConfirmed(true);
  };
  return (
    <Stack p={2} gap={2}>
      <Stack alignItems={"center"} direction="row" gap={1}>
        <Avatar
          sx={{
            width: 60,
            height: 60,
            marginRight: "10px",
          }}
        >
          <TargetsIcon size={"40"} />
        </Avatar>
        <Stack>
          <Typography variant="h4">Add Tenant</Typography>
        </Stack>
      </Stack>
      <Stack className="floating" gap={3}>
        <Typography variant="h5">Platform</Typography>
        <Stack gap={1}>
          <SidePanel
            open={panelView === "platform"}
            closeDrawer={() => setPanelView(false)}
            props={{ platform, setPlatform }}
          >
            {SelectPlatform}
          </SidePanel>
          <ActionButton
            icon="cloud"
            dark
            noload
            handler={async () => {
              setPanelView("platform");
            }}
          >
            {!platform ? "No platform selected" : platform}
          </ActionButton>
        </Stack>
      </Stack>
      {platform ? (
        <Stack className="floating" gap={3}>
          <Typography variant="h5">Tenant Configuration</Typography>
          <Stack gap={1}>
            <Typography>
              Provide values for the tenant configuration properties. You can
              also upload a JSON file containing the values. The file must be a
              JSON object containing the properties and corresponding values.
            </Typography>
          </Stack>
          <Stack sx={{ height: "450px" }} gap={1}>
            <EditorTable
              noHeader
              noColumnMod
              noRowMod
              cellFlex
              noClearButton
              loading={false}
              data={tenantConfig}
              close={() => {
                setConfirmed(false);
                setTenantConfig([["property", "value"]]);
              }}
              save={(data) => {
                setConfirmed(false);
                setTenantConfig(data);
              }}
            />
          </Stack>
          <Stack gap={1}>
            <FileUpload
              sizeInKb={300}
              handleFileChange={(file) => {
                setConfirmed(false);
                const reader = new FileReader();
                reader.onload = (e) => {
                  try {
                    const result = e.target?.result;
                    if (typeof result !== "string")
                      throw new Error("Invalid file content");

                    const jsonData = JSON.parse(result);
                    let valid = false;
                    if (
                      typeof jsonData === "object" &&
                      !Array.isArray(jsonData) &&
                      Object.keys(jsonData)?.length > 0
                    ) {
                      valid = true;
                    }
                    if (!valid) {
                      return toast.error("uploaded config is not valid");
                    }

                    const configsValue = Object.keys(jsonData).reduce(
                      (a, c) => [
                        ...a,
                        { property: c, value: jsonData[c] || "" },
                      ],
                      []
                    );
                    let existingFields = configsValue
                      ?.filter((x) => x?.property)
                      ?.map((x) => x.property);
                    let blankFields = tenantConfig?.filter(
                      (x) =>
                        x?.property && !existingFields.includes(x?.property)
                    );
                    setTenantConfig([
                      ["property", "value"],
                      ...blankFields,
                      ...configsValue,
                    ]);
                  } catch (err) {
                    const errorMessage =
                      err instanceof Error
                        ? err.message
                        : "Unknown error occurred";
                    toast.error(errorMessage);
                  }
                };
                if (file) {
                  reader.readAsText(file);
                }
                return true;
              }}
              hideFileName
            >
              Upload Configuration JSON
            </FileUpload>
            {/* ) : null} */}
          </Stack>
        </Stack>
      ) : null}
      {/* <Stack className="floating" gap={3}>
        <Typography variant="h5">Setup</Typography>
        <Stack gap={1}>
          <SidePanel
            open={panelView === "platform"}
            closeDrawer={() => setPanelView(false)}
            props={{ platform, setPlatform }}
          >
            {SelectPlatform}
          </SidePanel>
          <ActionButton
            icon="cloud"
            dark
            noload
            handler={async () => {
              setPanelView("platform");
            }}
          >
            {!platform ? "No platform selected" : platform}
          </ActionButton>
        </Stack>
      </Stack> */}
      <Stack className="floating" gap={3}>
        <Typography variant="h5">Management</Typography>
        <Stack gap={1}>
          <ScopeInputs />
          {/* <TagInput {...{ size: "small", dark: true }} /> */}
        </Stack>
      </Stack>
      <Stack mt={2}>
        {confirmed ? (
          <Stack gap={1}>
            <Typography sx={{ textAlign: "right" }} variant="body2">
              Your tenant has been confirmed!
            </Typography>
            <div style={{ marginLeft: "auto", display: "flex" }}>
              <ActionButton
                icon="save"
                dark
                handler={async () => {
                  await addTenant();
                }}
              >
                Add Tenant
              </ActionButton>
            </div>
          </Stack>
        ) : (
          <div style={{ marginLeft: "auto" }}>
            <ActionButton
              icon="science"
              dark
              noload
              handler={async () => {
                let response = await confirmTenant();
                return response;
              }}
            >
              Confirm Tenant Configuration
            </ActionButton>
          </div>
        )}
      </Stack>
      {/* {platform && e1tenant ? (
        <Stack className="floating" gap={3}>
          <Typography variant="h5">Instructions</Typography>
          <Stack gap={1}>
            <ReactMarkdown
              linkTarget="_blank"
              className="react-markdown-custom"
            >
              {instructions[platform]
                ? instructions[platform](e1tenant)
                : CustomInstruction}
            </ReactMarkdown>
          </Stack>
        </Stack>
      ) : null} */}
      <div style={{ height: "100px" }} />
    </Stack>
  );
}
